import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'blamonday:wishlist';
const UPDATE_EVENT = 'blamonday:wishlist:update';

const getWishlistItems = () => {
  try {
    const rawItems = window.localStorage.getItem(STORAGE_KEY);
    const items = rawItems ? JSON.parse(rawItems) : [];
    return Array.isArray(items) ? items : [];
  } catch (_error) {
    return [];
  }
};

const setWishlistItems = (items) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (_error) {
    // localStorage may be unavailable in some private browsing contexts.
  }
};

const dispatchWishlistUpdate = () => {
  document.dispatchEvent(
    new CustomEvent(UPDATE_EVENT, {
      detail: { items: getWishlistItems() },
    })
  );
};

const getVariantUrl = (url, variantId) => {
  if (!url || !variantId) return url || '';

  try {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set('variant', variantId);
    return nextUrl.pathname + nextUrl.search + nextUrl.hash;
  } catch (_error) {
    return url;
  }
};

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(UPDATE_EVENT, this.#syncState);
    window.addEventListener('storage', this.#handleStorage);

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.#syncState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(UPDATE_EVENT, this.#syncState);
    window.removeEventListener('storage', this.#handleStorage);

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    target?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  #handleStorage = (event) => {
    if (event.key === STORAGE_KEY) {
      dispatchWishlistUpdate();
    }
  };

  #handleVariantSelected = (event) => {
    const variantId = this.#getVariantIdFromPicker(event.target);
    if (variantId) {
      this.dataset.currentVariantId = variantId;
    }

    requestAnimationFrame(this.#syncState);
  };

  #handleVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (variant?.id) {
      this.dataset.currentVariantId = String(variant.id);
    }

    const variantImage = variant?.featured_media?.preview_image?.src;
    if (variantImage) {
      this.dataset.productImage = variantImage;
    }

    requestAnimationFrame(this.#syncState);
  };

  #handleClick = () => {
    const item = this.#getWishlistItem();
    if (!item) return;

    const items = getWishlistItems();
    const itemIndex = items.findIndex((existingItem) => existingItem.key === item.key);

    if (itemIndex >= 0) {
      items.splice(itemIndex, 1);
    } else {
      items.unshift(item);
    }

    setWishlistItems(items);
    dispatchWishlistUpdate();
  };

  #getVariantId() {
    if (this.dataset.currentVariantId) return this.dataset.currentVariantId;

    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    if (urlVariantId) return urlVariantId;

    const pickerVariantId = this.#getVariantIdFromPicker();
    if (pickerVariantId) return pickerVariantId;

    const productForm = this.closest('product-form-component');
    const variantInput = productForm?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) return variantInput.value;

    return this.dataset.variantId || '';
  }

  #getVariantIdFromPicker(eventTarget = null) {
    const picker =
      eventTarget instanceof Element && eventTarget.matches('variant-picker')
        ? eventTarget
        : this.#getVariantPicker();

    if (!(picker instanceof Element)) return '';

    const selectedOption = picker.querySelector('select option:checked[data-variant-id]');
    if (selectedOption instanceof HTMLOptionElement && selectedOption.dataset.variantId) {
      return selectedOption.dataset.variantId;
    }

    const checkedInput = picker.querySelector('input:checked[data-variant-id]');
    if (checkedInput instanceof HTMLInputElement && checkedInput.dataset.variantId) {
      return checkedInput.dataset.variantId;
    }

    return '';
  }

  #getWishlistItem() {
    const productId = this.dataset.productId;
    const variantId = this.#getVariantId();
    if (!productId || !variantId) return null;

    return {
      key: `${productId}:${variantId}`,
      productId,
      variantId,
      handle: this.dataset.productHandle || '',
      title: this.dataset.productTitle || '',
      url: getVariantUrl(this.dataset.productUrl || '', variantId),
      image: this.#getSelectedSwatchImage() || this.#getVariantImage(variantId) || this.dataset.productImage || '',
      addedAt: new Date().toISOString(),
    };
  }

  #getSelectedSwatchImage() {
    const picker = this.#getVariantPicker();
    if (!(picker instanceof Element)) return '';

    const checkedSwatchInput = picker.querySelector('.variant-option__button-label--has-swatch input:checked');
    const swatch = checkedSwatchInput?.closest('.variant-option__button-label--has-swatch')?.querySelector('.swatch');
    if (!(swatch instanceof HTMLElement)) return '';

    const swatchBackground =
      swatch.style.getPropertyValue('--swatch-background') ||
      getComputedStyle(swatch).getPropertyValue('--swatch-background');

    return this.#getUrlFromCssBackground(swatchBackground);
  }

  #getUrlFromCssBackground(background) {
    const match = background.match(/url\((['"]?)(.*?)\1\)/);
    const url = match?.[2]?.trim();
    if (!url) return '';

    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  #getVariantImage(variantId) {
    const script = this.querySelector('[data-wishlist-variant-images]');
    if (!script?.textContent) return '';

    try {
      const variants = JSON.parse(script.textContent);
      if (!Array.isArray(variants)) return '';

      const variant = variants.find((item) => String(item.id) === String(variantId));
      return typeof variant?.image === 'string' ? variant.image : '';
    } catch (_error) {
      return '';
    }
  }

  #getVariantPicker() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section, dialog, product-card');
    return section?.querySelector(`variant-picker${productId ? `[data-product-id="${productId}"]` : ''}`) || null;
  }

  #syncState = () => {
    const item = this.#getWishlistItem();
    const isSaved = item ? getWishlistItems().some((existingItem) => existingItem.key === item.key) : false;
    const button = this.querySelector('button');
    const label = isSaved ? 'お気に入りから削除' : 'お気に入りに追加';

    this.classList.toggle('is-active', isSaved);
    if (button instanceof HTMLButtonElement) {
      button.classList.toggle('is-active', isSaved);
      button.setAttribute('aria-pressed', String(isSaved));
      button.setAttribute('aria-label', label);
      button.title = label;
    }
  };
}

class WishlistDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(UPDATE_EVENT, this.#handleUpdate);
    window.addEventListener('storage', this.#handleStorage);
    this.addEventListener('click', this.#handleDrawerClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(UPDATE_EVENT, this.#handleUpdate);
    window.removeEventListener('storage', this.#handleStorage);
    this.removeEventListener('click', this.#handleDrawerClick);
  }

  open() {
    this.#render();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #handleUpdate = () => {
    this.#render();
  };

  #handleStorage = (event) => {
    if (event.key === STORAGE_KEY) {
      dispatchWishlistUpdate();
    }
  };

  #handleDrawerClick = (event) => {
    const removeButton = event.target?.closest?.('[data-wishlist-remove]');
    if (!(removeButton instanceof HTMLButtonElement)) return;

    event.stopImmediatePropagation();

    const key = removeButton.dataset.wishlistRemove;
    if (!key) return;

    setWishlistItems(getWishlistItems().filter((item) => item.key !== key));
    dispatchWishlistUpdate();
  };

  #render() {
    const items = getWishlistItems();
    const count = items.length;
    const countElements = document.querySelectorAll('[data-wishlist-count]');
    const bubbleElements = document.querySelectorAll('[data-wishlist-bubble]');
    const iconElements = document.querySelectorAll('[data-wishlist-icon]');
    const list = this.querySelector('[data-wishlist-list]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const dialog = this.refs.dialog;

    countElements.forEach((element) => {
      element.textContent = String(count);
    });
    bubbleElements.forEach((element) => {
      element.toggleAttribute('hidden', count === 0);
    });
    iconElements.forEach((element) => {
      element.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);
    });

    if (dialog instanceof HTMLDialogElement) {
      dialog.classList.toggle('wishlist-drawer--empty', count === 0);
    }

    if (!(list instanceof HTMLElement) || !(empty instanceof HTMLElement)) return;

    list.replaceChildren();
    empty.toggleAttribute('hidden', count > 0);
    list.toggleAttribute('hidden', count === 0);

    items.forEach((item) => {
      list.appendChild(this.#createItem(item));
    });
  }

  #createItem(item) {
    const row = document.createElement('li');
    row.className = 'wishlist-drawer__item';
    const itemUrl = getVariantUrl(item.url || `/products/${item.handle}`, item.variantId);

    const imageLink = document.createElement('a');
    imageLink.className = 'wishlist-drawer__image-link';
    imageLink.href = itemUrl;
    imageLink.setAttribute('aria-label', item.title || 'View product');

    if (item.image) {
      const image = document.createElement('img');
      image.className = 'wishlist-drawer__image';
      image.src = item.image;
      image.alt = item.title || '';
      image.loading = 'lazy';
      imageLink.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'wishlist-drawer__image wishlist-drawer__image--placeholder';
      imageLink.appendChild(placeholder);
    }

    const details = document.createElement('div');
    details.className = 'wishlist-drawer__details';

    const title = document.createElement('a');
    title.className = 'wishlist-drawer__title';
    title.href = imageLink.href;
    title.textContent = item.title || 'Product';

    const actions = document.createElement('div');
    actions.className = 'wishlist-drawer__item-actions';

    const view = document.createElement('a');
    view.className = 'wishlist-drawer__view-link';
    view.href = imageLink.href;
    view.textContent = '商品を見る';

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'wishlist-drawer__remove button-unstyled';
    remove.dataset.wishlistRemove = item.key;
    remove.textContent = '削除';

    actions.append(view, remove);
    details.append(title, actions);
    row.append(imageLink, details);

    return row;
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
